import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [showAllChapters, setShowAllChapters] = useState(false);

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);

  if (!comic) {
    return (
      <main className="pt-20 pb-12">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要查找的漫画不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const displayedChapters = showAllChapters
    ? comic.chapters
    : comic.chapters.slice(-10);
  const sortedChapters = [...comic.chapters].reverse();

  const bookJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `https://www.hanmanhua.com/comic/${comic.id}`,
    inLanguage: 'zh-CN',
    genre: comic.category,
  };

  return (
    <>
      <JsonLd data={bookJsonLd} />
      <main className="pt-20 pb-12">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${comic.category}韩漫`, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-8 mb-12">
            <div className="w-full md:w-[280px] shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 韩漫免费阅读`}
                  title={`${comic.title} - 无删减漫画免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="text-sm text-foreground-600">
                  作者：<strong>{comic.author}</strong>
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="text-sm text-foreground-600">
                  分类：<strong>{comic.category}</strong>
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="flex items-center gap-1 text-sm text-accent-500">
                  <i className="ri-star-fill text-xs"></i>
                  <strong>{comic.rating}</strong>
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="text-sm text-foreground-600">
                  状态：<strong>{comic.status}</strong>
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-foreground-500">
                <span>
                  <i className="ri-eye-line mr-1"></i>
                  {(comic.views / 10000).toFixed(1)}万 阅读
                </span>
                <span>
                  <i className="ri-file-list-3-line mr-1"></i>
                  共 {comic.totalChapters} 话
                </span>
                <span>
                  <i className="ri-time-line mr-1"></i>
                  更新于 {comic.updateDate}
                </span>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {comic.chapters.find((ch) => ch.isNew) && (
                  <button className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
                    开始阅读最新章节
                  </button>
                )}
                <button className="px-8 py-3 bg-secondary-500 text-background-50 font-medium rounded-lg hover:bg-secondary-600 transition-colors whitespace-nowrap">
                  从第一话开始
                </button>
              </div>
            </div>
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">
              章节列表
            </h2>
            <div className="space-y-1">
              {displayedChapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-background-100 transition-colors group cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {chapter.isNew && (
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded whitespace-nowrap">
                        NEW
                      </span>
                    )}
                    <span className="text-sm text-foreground-800 truncate">
                      {chapter.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 shrink-0 ml-4">
                    {chapter.updateDate}
                  </span>
                </div>
              ))}
            </div>
            {comic.chapters.length > 10 && (
              <button
                onClick={() => setShowAllChapters(!showAllChapters)}
                className="mt-4 w-full py-2.5 text-sm text-primary-600 hover:text-primary-700 bg-background-100 rounded-lg hover:bg-background-200 transition-colors font-medium"
              >
                {showAllChapters ? '收起章节列表' : `展开全部 ${comic.chapters.length} 话`}
              </button>
            )}
          </div>

          <div className="mt-10">
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-5">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {comics
                .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
                .slice(0, 6)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/comic/${related.id}`}
                    className="group"
                    data-product-shop
                  >
                    <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-foreground-800 truncate group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h3>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}